/**
 * Header Component
 *
 * Fixed header with breadcrumbs, search, notifications, tenant switcher, and user menu.
 * Features:
 * - Fixed position at top
 * - Height: 64px
 * - Responsive layout
 * - Mobile hamburger menu
 */

import { useState } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Breadcrumbs } from "./Breadcrumbs";
import { ThemeToggle } from "./ThemeToggle";
import { NotificationBell } from "./NotificationBell";
import { TenantSwitcher } from "./TenantSwitcher";
import { UserMenu } from "./UserMenu";
import { MobileMenu } from "./MobileMenu";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";

interface HeaderProps {
  sidebarCollapsed?: boolean;
}

export function Header({ sidebarCollapsed: _sidebarCollapsed }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      {/* `sticky top-0`: below md the document is the scroller (see `AppLayout`), so the
          header would scroll away with the content without it. At >=md the header sits in
          the non-scrolling shell and sticky is inert. */}
      <header className="sticky top-0 h-16 border-b bg-background flex-shrink-0 z-40">
        <div className="flex h-full items-center justify-between px-4 md:px-6">
          {/* Left Section */}
          <div className="flex items-center gap-4">
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Breadcrumbs */}
            <div className="hidden sm:block">
              <Breadcrumbs />
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2">
            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Notification Bell */}
            <NotificationBell />

            {/* Tenant Switcher - Hidden on mobile */}
            <div className="hidden lg:block">
              <TenantSwitcher />
            </div>

            {/* User Menu */}
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <MobileMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />
    </>
  );
}
